<template>
  <section class="bg-[radial-gradient(circle_at_top_left,#fff9f1_0%,#f8f1ea_36%,#f3ebe3_100%)] py-4 sm:py-5 lg:py-6">
    <div class="mx-auto max-w-[1480px] px-4 sm:px-5 lg:px-6">
      <div v-if="loading" class="rounded-[28px] border border-dashed border-[#eadfd4] bg-white/90 p-8 text-sm text-muted shadow-sm">
        Loading venue preview...
      </div>

      <div v-else class="space-y-5 lg:space-y-6">
        <nav class="flex flex-wrap items-center gap-2 text-sm text-[#7d6653]">
          <button type="button" class="inline-flex items-center gap-2 rounded-full border border-[#e4d6ca] bg-white px-4 py-2 font-semibold text-ink shadow-sm transition hover:border-brand hover:text-brand" @click="goBack">
            <i class="ri-arrow-left-line"></i>
            Back
          </button>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">Business</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-medium">{{ businessName }}</span>
          <span class="text-[#ccb8a8]">/</span>
          <span class="font-semibold text-ink">{{ venue.title || 'Venue detail' }}</span>
        </nav>

        <div v-if="error" class="rounded-[22px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800">
          {{ error }}
        </div>

        <section class="overflow-hidden rounded-[28px] border border-[#eadfd4] bg-white shadow-sm">
          <div class="border-b border-[#efe3d8] px-5 py-4 lg:px-6">
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 class="mt-2 font-serif text-[2rem] font-semibold leading-tight text-ink lg:text-[2.6rem]">{{ venue.title || 'Venue detail' }}</h1>
                <p class="mt-2 text-[1rem] italic text-[#927660]">{{ venue.tagLine || 'Venue preview for the public detail page.' }}</p>
              </div>
              <span class="rounded-full bg-[#fbf1e6] px-4 py-2 text-sm font-semibold text-brand">
                {{ venue.type || 'Venue' }}
              </span>
            </div>
          </div>

          <div class="space-y-3 px-5 py-4 lg:px-6">
            <div class="mx-auto grid max-w-[980px] gap-2.5 sm:gap-3 lg:grid-cols-4 lg:grid-rows-2">
              <button
                type="button"
                class="relative min-h-[220px] overflow-hidden rounded-[28px] text-left sm:min-h-[240px] lg:col-span-2 lg:row-span-2 lg:h-[400px] lg:min-h-0"
                @click="openVenueGalleryById(previewGalleryTiles[0])"
              >
                <img
                  v-if="previewGalleryTiles[0]"
                  :src="previewGalleryTiles[0].src"
                  :alt="venue.title"
                  class="h-full w-full rounded-[28px] bg-[#f5efe8] object-cover"
                  :class="{ 'object-contain p-5': previewGalleryTiles[0].isPlaceholder }"
                >
                <span v-if="previewHeroTileIsCover" class="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-brand shadow-sm">
                  Cover image
                </span>
              </button>

              <button
                v-for="(image, index) in previewGalleryTiles.slice(1, 5)"
                :key="`${image.id || 'hero-side'}-${index}`"
                type="button"
                class="relative min-h-[140px] overflow-hidden rounded-[24px] text-left sm:min-h-[150px] lg:h-[192px] lg:min-h-0"
                @click="openVenueGalleryById(image)"
              >
                <img
                  :src="image.src"
                  :alt="`${venue.title} ${index + 2}`"
                  class="h-full w-full rounded-[24px] bg-[#f5efe8] object-cover"
                  :class="{ 'object-contain p-4': image.isPlaceholder }"
                >
                <div
                  v-if="image.overlayText"
                  class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white"
                >
                  <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                  <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
                </div>
              </button>
            </div>

            <div class="rounded-[28px] border border-[#eadfd4] bg-[#fffdf9] p-5">
              <div class="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#6f5948]">
                <span class="inline-flex items-center gap-2">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  {{ venue.address || 'Location unavailable' }}
                </span>
                <span class="text-[#d8c8bb]">·</span>
                <span>{{ establishedLabel }}</span>
                <span v-if="venue.phone" class="text-[#d8c8bb]">·</span>
                <span v-if="venue.phone" class="inline-flex items-center gap-2">
                  <i class="ri-phone-line text-brand"></i>
                  {{ venue.phoneCode ? `${venue.phoneCode} ` : '' }}{{ venue.phone }}
                </span>
              </div>

              <p class="mt-6 text-[1.05rem] leading-8 text-[#2f241b]">
                {{ venue.about || venueStory }}
              </p>

              <div v-if="venueHighlightsList.length" class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Highlights</p>
                <div class="mt-3 flex flex-wrap gap-2">
                  <span v-for="item in venueHighlightsList" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">
                    {{ item }}
                  </span>
                </div>
              </div>

              <div v-if="inHouseServicesList.length" class="mt-6">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">In-house services</p>
                <div class="mt-4 grid gap-3 md:grid-cols-2">
                  <div v-for="item in inHouseServicesList" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                    <span class="text-[#2e8a5d]">✓</span>
                    <span>{{ item }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div class="space-y-6">
            <section v-if="spaces.length" class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Spaces</p>
                <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue spaces</h2>
              </div>

              <div v-if="spaces.length" class="mt-5">
                <div class="flex flex-wrap gap-3">
                  <button
                    v-for="item in spaces"
                    :key="item.id"
                    type="button"
                    class="rounded-full border px-5 py-2.5 text-sm font-semibold transition"
                    :class="selectedSpaceId === item.id ? 'border-[#d9a441] bg-[#fff7e8] text-[#80550d] shadow-sm' : 'border-[#eadfd4] bg-white text-[#6f5948] hover:border-[#d9a441] hover:text-[#80550d]'"
                    @click="selectedSpaceId = item.id"
                  >
                    {{ item.spaceIcon }} {{ item.name }}
                  </button>
                </div>

                <article v-if="selectedSpace" class="mt-5 overflow-hidden rounded-[26px] border border-[#eadfd4] bg-[#fffdfa]">
                  <div class="flex flex-col gap-5 border-b border-[#eadfd4] px-6 py-6 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <h3 class="font-serif text-[2rem] font-semibold text-[#1f1208]">{{ selectedSpace.name }}</h3>
                      <div class="mt-4 flex flex-wrap gap-2">
                        <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.settingIcon }} {{ selectedSpace.setting || 'Indoor' }}</span>
                        <span class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">{{ selectedSpace.spaceTypeIcon }} {{ selectedSpace.spaceType || 'Space' }}</span>
                        <span v-if="selectedSpace.areaSqft" class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]">📐 {{ selectedSpace.areaSqft }} sqft</span>
                      </div>
                    </div>

                    <div class="text-left lg:text-right">
                      <p class="text-[2rem] font-semibold leading-none text-[#1f1208]">{{ selectedSpace.displayMinPrice }}</p>
                      <p v-if="selectedSpace.displayMaxPrice" class="mt-2 text-base text-[#8b715e]">to {{ selectedSpace.displayMaxPrice }} per event</p>
                    </div>
                  </div>

                  <div class="px-6 py-6">
                    <p class="text-[1.05rem] leading-8 text-[#2f241b]">
                      {{ selectedSpace.description || 'No description added.' }}
                    </p>

                    <div v-if="selectedSpace.seatingItems.length" class="mt-8">
                      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Seating configurations</p>
                      <div class="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <article v-for="item in selectedSpace.seatingItems" :key="`${selectedSpace.id}-${item.label}`" class="rounded-[18px] border border-[#eadfd4] bg-white px-4 py-4">
                          <div class="flex items-end justify-between gap-3">
                            <p class="text-[1rem] font-medium leading-6 text-[#2f241b]">{{ item.label }}</p>
                            <p class="text-lg font-semibold text-[#1f1208]">{{ item.capacity }}</p>
                          </div>
                        </article>
                      </div>
                    </div>

                    <div class="mt-8 grid gap-8 lg:grid-cols-2">
                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Included amenities</p>
                        <div class="mt-4 space-y-3">
                          <div v-for="item in selectedSpace.includedAmenities" :key="`${selectedSpace.id}-amenity-${item}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                            <span class="inline-flex items-center gap-3">
                              <span class="text-[#2e8a5d]">✓</span>
                              <span>{{ item }}</span>
                            </span>
                          </div>
                          <p v-if="!selectedSpace.includedAmenities.length" class="text-sm text-muted">No included amenities added yet.</p>
                        </div>
                      </div>

                      <div>
                        <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Equipment</p>
                        <div class="mt-4 space-y-3">
                          <div v-for="item in selectedSpace.equipmentItems" :key="`${selectedSpace.id}-equipment-${item.label}`" class="flex items-start justify-between gap-3 text-[1rem] text-[#2f241b]">
                            <span class="inline-flex items-center gap-3">
                              <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'">{{ item.included ? '✓' : '+' }}</span>
                              <span>{{ item.label }}</span>
                            </span>
                            <span :class="item.included ? 'text-[#2e8a5d]' : 'text-[#c58a27]'" class="text-sm font-semibold">
                              {{ item.included ? 'Included' : 'Optional' }}
                            </span>
                          </div>
                          <p v-if="!selectedSpace.equipmentItems.length" class="text-sm text-muted">No equipment details added yet.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>

            </section>

          <section v-if="packages.length" class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Packages</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue packages</h2>
            </div>

            <div v-if="packages.length" class="mt-5 grid gap-4 md:grid-cols-2">
              <article v-for="item in packages" :key="item.id" class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf5] p-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-lg font-semibold text-ink">{{ item.name }}</h3>
                    <div class="mt-2 flex flex-wrap gap-2 text-xs text-[#7d634e]">
                      <span class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.priceLabel }}</span>
                      <span v-if="item.maxGuests" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.maxGuests }} guests</span>
                      <span v-if="item.validHours" class="rounded-full bg-white px-3 py-1 font-semibold ring-1 ring-[#eadfd4]">{{ item.validHours }} hrs</span>
                    </div>
                  </div>
                </div>

                <p class="mt-4 text-sm leading-7 text-[#4b3b2f]">{{ item.description || 'No description added.' }}</p>

                <div v-if="item.includedServices.length" class="mt-4 flex flex-wrap gap-2">
                  <span v-for="service in item.includedServices" :key="service" class="rounded-full border border-[#eadfd4] bg-white px-3 py-1 text-xs font-semibold text-[#6f5948]">
                    {{ service }}
                  </span>
                </div>
              </article>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Logistics</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue logistics & transportation</h2>
            </div>

            <div class="mt-5 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue facts</p>
                <div class="mt-5 space-y-5">
                  <article class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">🛏️</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">Rooms</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ venue.noOfRooms || 'Room count not added yet.' }}</p>
                    </div>
                  </article>
                  <article class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">🚗</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">Parking</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ parkingSummary }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue timing</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in venueTimings" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">🕒</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                  <p v-if="!hasVenueTimings" class="text-[1.02rem] leading-7 text-[#2f241b]">Venue timing not added yet.</p>
                </div>
              </div>
            </div>

            <div v-if="additionalFacilitiesList.length" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Additional facilities</p>
              <div class="mt-4 space-y-3">
                <div v-for="item in additionalFacilitiesList" :key="item.label" class="flex items-start gap-3 text-[1rem] text-[#2f241b]">
                  <span :class="item.available ? 'text-[#2e8a5d]' : 'text-[#d64545]'" class="mt-0.5 text-base font-bold">
                    {{ item.available ? '✔' : '✖' }}
                  </span>
                  <span>{{ item.label }}</span>
                </div>
              </div>
            </div>

            <div v-if="hasTransportationDetails" class="mt-6">
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Transportation</p>
              <div v-if="nearbyAreasList.length" class="mt-4 grid gap-3 md:grid-cols-2">
                <div v-for="item in nearbyAreasList" :key="item" class="flex items-center gap-3 text-[1rem] text-[#2f241b]">
                  <i class="ri-map-pin-2-fill text-[#d16c57]"></i>
                  <span>{{ item }}</span>
                </div>
              </div>
              <a
                v-if="transportationMapLink"
                :href="transportationMapLink"
                target="_blank"
                rel="noreferrer"
                class="mt-4 inline-flex items-center gap-2 rounded-full border border-[#eadfd4] bg-[#fffaf5] px-4 py-2 text-sm font-semibold text-brand transition hover:border-brand"
              >
                <i class="ri-road-map-line"></i>
                Open saved Google Maps link
              </a>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Venue policies</h2>
            </div>

            <div class="mt-6 grid gap-8 md:grid-cols-2">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Venue rules</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in venueRuleItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>

              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Booking terms</p>
                <div class="mt-5 space-y-5">
                  <article v-for="item in bookingTermItems" :key="item.label" class="flex items-start gap-4">
                    <span class="mt-0.5 text-[1.35rem]">{{ item.icon }}</span>
                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.12em] text-[#b78969]">{{ item.label }}</p>
                      <p class="mt-1 text-[1.02rem] leading-7 text-[#2f241b]">{{ item.value }}</p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Dining</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Menu with Prices</h2>
              <p class="mt-4 text-[1rem] leading-7 text-[#2f241b]">
                Below listed prices are <strong>excluding all taxes.</strong>
              </p>
              <p class="text-[1rem] leading-7 text-[#2f241b]">
                Menu prices are subject to change, will be varies depending on guest requirement and number of people. Please contact manager for more details.
              </p>
            </div>

            <div class="mt-5 overflow-hidden rounded-[22px] border border-[#eadfd4]">
              <table class="min-w-full border-collapse bg-white text-left">
                <thead class="bg-[#fbf6f0]">
                  <tr>
                    <th class="border-b border-r border-[#eadfd4] px-4 py-3 text-sm font-semibold text-ink">Menu Name</th>
                    <th class="border-b border-[#eadfd4] px-4 py-3 text-sm font-semibold text-ink">Per Person</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="border-b border-r border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b]">
                      <span class="inline-flex items-center gap-2">
                        <span class="h-3.5 w-3.5 rounded-[4px] border border-[#1f9d45] bg-white p-[2px]">
                          <span class="block h-full w-full rounded-[2px] bg-[#1f9d45]"></span>
                        </span>
                        Veg
                      </span>
                    </td>
                    <td class="border-b border-[#eadfd4] px-4 py-3 text-[1rem] font-medium text-[#2f241b]">Rs.599</td>
                  </tr>
                  <tr>
                    <td class="border-r border-[#eadfd4] px-4 py-3 text-[1rem] text-[#2f241b]">
                      <span class="inline-flex items-center gap-2">
                        <span class="h-3.5 w-3.5 rounded-[4px] border border-[#d45711] bg-white p-[2px]">
                          <span class="block h-full w-full rounded-[2px] bg-[#d45711]"></span>
                        </span>
                        Non Veg
                      </span>
                    </td>
                    <td class="px-4 py-3 text-[1rem] font-medium text-[#2f241b]">Rs.799</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Location</p>
              <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">{{ venue.address || 'Venue location' }}</h2>
            </div>

            <div class="mt-5 overflow-hidden rounded-[24px] border border-[#d8e7ef] bg-[#dff0fa]">
              <div
                class="relative flex min-h-[220px] items-center justify-center px-6 py-10 text-center"
                style="background-image: linear-gradient(to right, rgba(130,160,180,0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(130,160,180,0.14) 1px, transparent 1px); background-size: 112px 100%, 100% 34px;"
              >
                <div class="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.22),transparent_58%)]"></div>
                <div class="relative z-10 flex flex-col items-center">
                  <div class="relative">
                    <span class="block h-5 w-5 rounded-full bg-[#ff5a49] shadow-[0_6px_14px_rgba(255,90,73,0.28)]"></span>
                    <span class="absolute left-1/2 top-4 h-8 w-[3px] -translate-x-1/2 rounded-full bg-[#4a89a8]"></span>
                  </div>
                  <p class="mt-10 font-serif text-[1.35rem] font-semibold text-[#2f241b]">{{ venue.address || 'Venue location not added yet.' }}</p>
                  <p class="mt-1 text-[1rem] text-[#5b6c76]">{{ venue.address || 'Hyderabad, Telangana' }}</p>
                  <a
                    href="#"
                    class="mt-5 inline-flex items-center gap-2 rounded-full bg-[#c99a22] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#b88817]"
                  >
                    Open in Google Maps
                    <i class="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

        </div>

        <aside class="self-start xl:sticky xl:top-24">
          <section class="rounded-[28px] border border-[#eadfd4] bg-white p-6 shadow-sm">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Preview</p>
            <h2 class="mt-2 font-serif text-[1.75rem] font-semibold text-ink">Public page summary</h2>
            <p class="mt-2 text-sm leading-7 text-muted">This preview mirrors the public venue detail layout so you can review what guests will see.</p>

            <div class="mt-5 space-y-4">
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Business</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ businessName }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Venue status</p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ venue.status || 'Draft' }}</p>
              </div>
              <div class="rounded-[22px] border border-[#eadfd4] bg-[#fffaf5] px-4 py-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#b78969]">Preview readiness</p>
                <div class="mt-3 space-y-2 text-sm text-[#4b3b2f]">
                  <div class="flex items-center justify-between gap-3">
                    <span>Gallery images</span>
                    <span class="font-semibold text-ink">{{ gallery.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Spaces configured</span>
                    <span class="font-semibold text-ink">{{ spaces.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Packages configured</span>
                    <span class="font-semibold text-ink">{{ packages.length }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span>Partners linked</span>
                    <span class="font-semibold text-ink">{{ partners.length }}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="mt-5 w-full rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
              @click="openVenueGallery()"
            >
              Open full gallery
            </button>
          </section>
        </aside>
      </div>
    </div>
  </div>
</section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import api from '@/services/vendor/api'

const route = useRoute()
const router = useRouter()

const loading = ref(false)
const error = ref('')
const businessName = ref(String(route.query.businessName || 'Business'))
const venue = ref({
  id: '',
  title: '',
  type: '',
  tagLine: '',
  about: '',
  address: '',
  status: '',
  yearEstablished: '',
  phone: '',
  phoneCode: '',
  email: '',
  website: '',
  highlights: [],
  inHouseServices: []
})
const spaces = ref([])
const packages = ref([])
const partners = ref([])
const gallery = ref([])
const galleryLightbox = ref(null)
const selectedSpaceId = ref('')
const noImagesPlaceholderSrc = '/assets/icons/no-images.svg'
const policies = ref({
  inHouseCatering: '',
  outsideCatering: '',
  cateringFoodType: '',
  cateringPriceUnit: '',
  decorPolicy: '',
  musicPolicy: '',
  alcoholInhouse: '',
  alcoholOutside: '',
  minGuests: '',
  maxGuests: '',
  decorTime: '',
  cleanTime: '',
  noiseMusic: '',
  otherHouseRules: '',
  cancelTerms: '',
  depositPercent: ''
})

const previewPlaceholderImages = [
  {
    id: 'placeholder-1',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-2',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-3',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-4',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  },
  {
    id: 'placeholder-5',
    src: noImagesPlaceholderSrc,
    tagName: 'No image available',
    isCover: false,
    isPlaceholder: true
  }
]

const normalizeApiCollection = (data, keys = []) => {
  const root = data?.data ?? data
  if (Array.isArray(root)) return root
  for (const key of keys) {
    if (Array.isArray(root?.[key])) return root[key]
  }
  return []
}

const splitToArray = (value) =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)

const normalizeVenue = (item = {}) => ({
  id: String(item?.id || item?.venue_id || item?.vendor_business_venue_id || route.params.venueId || ''),
  title: item?.title || item?.name || item?.venue_name || 'Untitled venue',
  type: item?.type_name || item?.type || item?.venue_type_name || item?.venue_type || '',
  tagLine: item?.tag_line || item?.tagLine || '',
  about: item?.about || item?.description || '',
  address: item?.fulladdress || item?.address || item?.location || '',
  status: String(item?.status || '').trim() === '1' ? 'Active' : item?.status || 'Draft',
  yearEstablished: item?.est_year || item?.year_established || item?.yearEstablished || '',
  phone: item?.phone || item?.mobile || item?.contact_number || '',
  phoneCode: item?.phone_code || item?.phoneCode || '',
  email: item?.email || '',
  website: item?.website || '',
  highlights: splitToArray(item?.highlights || item?.venue_highlights),
  inHouseServices: splitToArray(item?.in_house_services || item?.inHouseServices),
  additionalFacilities: splitToArray(item?.additional_facilities || item?.additionalFacilities),
  noOfRooms: item?.no_of_rooms || item?.rooms_count || '',
  parkingAvailable: item?.car_parking_available || item?.parking_available || '',
  parkingVehicleCount: item?.parking_vehicle_count || item?.no_of_vehicles || '',
  morningTimingFrom: item?.venue_timing_morning_from || item?.morning_timing_from || '',
  morningTimingTo: item?.venue_timing_morning_to || item?.morning_timing_to || '',
  eveningTimingFrom: item?.venue_timing_evening_from || item?.evening_timing_from || '',
  eveningTimingTo: item?.venue_timing_evening_to || item?.evening_timing_to || '',
  nearbyAreas: splitToArray(item?.nearby_areas || item?.transportation_nearby_areas),
  transportationMapLink: item?.transportation_map_link || item?.google_maps_link || item?.map_link || ''
})

const normalizeSpace = (item = {}, index = 0) => {
  const configurations = splitToArray(item?.seat_config || item?.seatConfig)
  const capacities = splitToArray(item?.seat_capacity || item?.seatCapacity)
  const amenities = splitToArray(item?.amenities)
  const spaceType = item?.space_type || item?.spaceType || ''
  const setting = item?.setting || 'Indoor'
  const seatingItems = configurations.map((label, configIndex) => ({
    label,
    capacity: capacities[configIndex] || 'N/A'
  }))
  const includedAmenities = amenities.slice(0, 6)
  const equipmentItems = amenities.slice(6).map((label) => ({
    label,
    included: true
  }))
  return {
    id: String(item?.space_id || item?.id || `space-${index}`),
    name: item?.name || 'Untitled space',
    spaceType,
    setting,
    spaceIcon: inferSpaceIcon(spaceType || item?.name || ''),
    spaceTypeIcon: inferSpaceIcon(spaceType || ''),
    settingIcon: inferSpaceIcon(setting),
    areaSqft: item?.area_sqft || item?.areaSqft || '',
    description: item?.description || '',
    minPrice: item?.min_price || item?.minPrice || '',
    maxPrice: item?.max_price || item?.maxPrice || '',
    displayMinPrice: formatCurrency(item?.min_price || item?.minPrice) || 'Price on request',
    displayMaxPrice: formatCurrency(item?.max_price || item?.maxPrice) || '',
    priceLabel: formatPriceRange(item?.min_price || item?.minPrice, item?.max_price || item?.maxPrice),
    amenities,
    seatingItems,
    includedAmenities,
    equipmentItems,
    seatingSummary: configurations.map((label, configIndex) => {
      const capacity = capacities[configIndex]
      return capacity ? `${label}: ${capacity}` : label
    })
  }
}

const normalizePackage = (item = {}, index = 0) => ({
  id: String(item?.package_id || item?.id || `package-${index}`),
  name: item?.name || 'Untitled package',
  description: item?.description || '',
  price: item?.price || '',
  maxGuests: item?.max_guests || item?.maxGuests || '',
  validHours: item?.valid_hours || item?.validHours || '',
  includedServices: splitToArray(item?.included_services || item?.includedServices),
  priceLabel: formatCurrency(item?.price) || 'Price on request'
})

const normalizePartner = (item = {}, index = 0) => ({
  id: String(item?.vendor_partner_id || item?.id || `partner-${index}`),
  name: item?.name || 'Partner',
  category: item?.category || '',
  phone: item?.phone || ''
})

const normalizeVenueGalleryEntries = (source) => {
  const items = Array.isArray(source) ? source : [source]
  return items
    .flatMap((item, itemIndex) => {
      if (!item) return []
      if (typeof item === 'string') {
        return [{
          id: `gallery-${itemIndex}-${item}`,
          src: item,
          tagName: '',
          isCover: false
        }]
      }
      const imageKeys = ['image1', 'image2', 'image3', 'image4', 'image', 'image_url', 'url', 'path', 'img', 'src']
      const entryId = String(item?.id || item?.gallery_id || item?.venue_gallery_id || item?.img_id || '')
      return imageKeys
        .map((key, keyIndex) => {
          const src = item?.[key] || ''
          if (!src) return null
          return {
            id: entryId || `gallery-${itemIndex}-${keyIndex}-${src}`,
            src,
            tagName: String(item?.tag_name || item?.tagName || item?.name || '').trim(),
            isCover: String(item?.is_cover || item?.isCover || '0') === '1'
          }
        })
        .filter(Boolean)
    })
    .filter((entry) => entry?.src)
}

const formatCurrency = (value) => {
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0 ? `Rs. ${numeric.toLocaleString('en-IN')}` : ''
}

const formatPriceRange = (minPrice, maxPrice) => {
  const minValue = formatCurrency(minPrice)
  const maxValue = formatCurrency(maxPrice)
  if (minValue && maxValue) return `${minValue} - ${maxValue}`
  return minValue || maxValue || 'Price on request'
}

const inferSpaceIcon = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return '🏛️'
  if (normalized.includes('lawn') || normalized.includes('garden')) return '🌿'
  if (normalized.includes('terrace') || normalized.includes('rooftop')) return '⛱️'
  if (normalized.includes('pool')) return '🏊'
  if (normalized.includes('outdoor')) return '☀️'
  if (normalized.includes('indoor')) return '🏠'
  if (normalized.includes('hall') || normalized.includes('ballroom')) return '🏛️'
  return '🏛️'
}

const venueCity = computed(() => {
  const address = String(venue.value.address || '').trim()
  if (!address) return 'City'
  const segments = address.split(',').map((item) => item.trim()).filter(Boolean)
  return segments.length >= 2 ? segments[segments.length - 2] : segments[0] || 'City'
})

const establishedLabel = computed(() => venue.value.yearEstablished ? `Est. ${venue.value.yearEstablished}` : 'Est. Not set')
const selectedSpace = computed(() => {
  if (!spaces.value.length) return null
  return spaces.value.find((item) => item.id === selectedSpaceId.value) || spaces.value[0]
})

const previewGalleryTiles = computed(() => {
  const sortedGallery = [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
  const baseTiles = sortedGallery.slice(0, 5)
  const missing = Math.max(0, 5 - baseTiles.length)
  const filledTiles = [...baseTiles, ...previewPlaceholderImages.slice(0, missing)].map((item, index) => ({
    ...item,
    id: item.id || `preview-tile-${index}`,
    isPlaceholder: Boolean(item.isPlaceholder),
    overlayText: ''
  }))

  if (!filledTiles.length) {
    return previewPlaceholderImages.map((item, index) => ({
      ...item,
      id: item.id || `preview-tile-${index}`,
      overlayText: ''
    }))
  }

  const hiddenCount = Math.max(0, sortedGallery.length - 5)
  if (filledTiles[4]) {
    filledTiles[4] = {
      ...filledTiles[4],
      overlayText: hiddenCount ? `+${hiddenCount}` : ''
    }
  }

  return filledTiles
})
const previewHeroTileIsCover = computed(() => Boolean(previewGalleryTiles.value[0] && previewGalleryTiles.value[0].isCover))

const lightboxGalleryImages = computed(() => {
  const source = gallery.value.length
    ? [...gallery.value].sort((left, right) => Number(right.isCover) - Number(left.isCover))
    : previewPlaceholderImages

  return source.map((item, index) => ({
    id: item.id || `lightbox-${index}`,
    src: item.src,
    thumb: item.src,
    tagName: item.tagName || '',
    isCover: Boolean(item.isCover),
    isPlaceholder: Boolean(item.isPlaceholder)
  }))
})

const venueStory = computed(() => (
  venue.value.about
    || `Established to host memorable celebrations in ${venueCity.value}, ${venue.value.title || 'this venue'} brings together flexible spaces, hospitality support, and a layout designed for weddings, parties, and social events.`
))

const venueHighlightsList = computed(() => {
  const items = venue.value.highlights.length
    ? venue.value.highlights
    : ['Award Winning', '5-Star Rated', 'Valet Parking', 'Wheelchair Accessible', 'Power Backup', 'CCTV Surveillance']
  return items.slice(0, 8)
})

const inHouseServicesList = computed(() => {
  const packageServices = packages.value.flatMap((item) => item.includedServices || [])
  const venueServices = Array.isArray(venue.value.inHouseServices) ? venue.value.inHouseServices : []
  const servicePool = [...venueServices, ...packageServices]
    .map((item) => String(item || '').trim())
    .filter(Boolean)

  const uniqueServices = [...new Set(servicePool)]
  return uniqueServices.length
    ? uniqueServices.slice(0, 8)
    : ['Catering', 'DJ / Music', 'Valet Parking', 'Event Coordination', 'Decoration', 'Photography', 'Bar', 'Guest Support']
})
const additionalFacilitiesList = computed(() => {
  const items = Array.isArray(venue.value.additionalFacilities) ? venue.value.additionalFacilities : []
  return items
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .map((item) => ({
      label: item,
      available: !/not available|not provided|unavailable|not allowed|no\b/i.test(item)
    }))
})
const nearbyAreasList = computed(() => {
  const items = Array.isArray(venue.value.nearbyAreas) ? venue.value.nearbyAreas : []
  return items.map((item) => String(item || '').trim()).filter(Boolean)
})
const transportationMapLink = computed(() => String(venue.value.transportationMapLink || '').trim())
const parkingSummary = computed(() => {
  const availability = String(venue.value.parkingAvailable || '').trim()
  const vehicleCount = String(venue.value.parkingVehicleCount || '').trim()
  if (!availability) return 'Parking details not added yet.'
  return vehicleCount ? `${availability} · ${vehicleCount} vehicles` : availability
})
const formatAlcoholPolicyValue = (value, enabledLabel, disabledLabel) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  if (['1', 'yes', 'true'].includes(normalized)) return enabledLabel
  if (['0', 'no', 'false'].includes(normalized)) return disabledLabel
  return String(value || '').trim()
}
const alcoholPolicySummary = computed(() => {
  const items = [
    formatAlcoholPolicyValue(
      policies.value.alcoholInhouse,
      'In-house bar / alcohol available: Yes',
      'In-house bar / alcohol available: No'
    ),
    formatAlcoholPolicyValue(
      policies.value.alcoholOutside,
      'Outside alcohol permitted: Yes',
      'Outside alcohol permitted: No'
    )
  ].filter(Boolean)

  return items.join(' · ') || 'Alcohol policy not added yet.'
})
const venueTimings = computed(() => ([
  {
    label: 'Morning / Lunch',
    value: venue.value.morningTimingFrom && venue.value.morningTimingTo
      ? `${venue.value.morningTimingFrom} - ${venue.value.morningTimingTo}`
      : ''
  },
  {
    label: 'Evening / Dinner',
    value: venue.value.eveningTimingFrom && venue.value.eveningTimingTo
      ? `${venue.value.eveningTimingFrom} - ${venue.value.eveningTimingTo}`
      : ''
  }
]).filter((item) => item.value))
const hasVenueTimings = computed(() => venueTimings.value.length > 0)
const hasTransportationDetails = computed(() => nearbyAreasList.value.length > 0 || Boolean(transportationMapLink.value))

const venueRuleItems = computed(() => ([
  {
    icon: '🍽️',
    label: 'Catering',
    value: [
      policies.value.inHouseCatering,
      policies.value.outsideCatering,
      policies.value.cateringFoodType,
      policies.value.cateringPriceUnit
    ].filter(Boolean).join('. ') || 'Catering policy not added yet.'
  },
  {
    icon: '🌸',
    label: 'Decoration',
    value: policies.value.decorPolicy || 'Decoration policy not added yet.'
  },
  {
    icon: '🎵',
    label: 'DJ / Music',
    value: policies.value.musicPolicy || 'Music policy not added yet.'
  },
  {
    icon: '🍸',
    label: 'Alcohol',
    value: alcoholPolicySummary.value
  },
  {
    icon: '🔊',
    label: 'Noise curfew',
    value: policies.value.noiseMusic ? `Music must end by ${policies.value.noiseMusic}.` : 'Noise or music curfew not added yet.'
  },
  {
    icon: '📋',
    label: 'House rules',
    value: policies.value.otherHouseRules || 'No additional house rules added yet.'
  }
]))

const bookingTermItems = computed(() => ([
  {
    icon: '👥',
    label: 'Guest count',
    value: policies.value.minGuests || policies.value.maxGuests
      ? `${policies.value.minGuests || 'Flexible'}-${policies.value.maxGuests || 'Flexible'} guests`
      : 'Guest count policy not added yet.'
  },
  {
    icon: '🔧',
    label: 'Setup time',
    value: policies.value.decorTime || 'Setup timing not added yet.'
  },
  {
    icon: '🧹',
    label: 'Cleanup time',
    value: policies.value.cleanTime || 'Cleanup timing not added yet.'
  },
  {
    icon: '💳',
    label: 'Deposit',
    value: policies.value.depositPercent ? `${policies.value.depositPercent}% of total to confirm booking` : 'Deposit terms not added yet.'
  },
  {
    icon: '↩️',
    label: 'Cancellation',
    value: policies.value.cancelTerms || 'Cancellation policy not added yet.'
  }
]))

const loadVenueGalleryPreview = async (venueId) => {
  const categoriesResponse = await api.getVenueGalleryCategories(new FormData())
  const categories = normalizeApiCollection(categoriesResponse?.data, ['categories', 'gallery_categories', 'items', 'list'])
  const tagIds = categories
    .map((item) => String(item?.id || item?.gallery_category_id || item?.tag_id || '').trim())
    .filter(Boolean)

  if (!tagIds.length) {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    const galleryResponse = await api.getVenueGallery(formData)
    const payload = galleryResponse?.data?.data || galleryResponse?.data || {}
    const items = normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list'])
    return normalizeVenueGalleryEntries(items.length ? items : payload)
  }

  const responses = await Promise.all(tagIds.map(async (tagId) => {
    const formData = new FormData()
    formData.append('venue_id', venueId)
    formData.append('tag', tagId)
    const galleryResponse = await api.getVenueGallery(formData)
    const payload = galleryResponse?.data?.data || galleryResponse?.data || {}
    const items = normalizeApiCollection(galleryResponse?.data, ['gallery', 'images', 'items', 'list'])
    return normalizeVenueGalleryEntries(items.length ? items : payload)
  }))

  const seen = new Set()
  return responses.flat().filter((item) => {
    const key = `${item.id}-${item.src}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

const loadVenue = async () => {
  loading.value = true
  error.value = ''

  try {
    const businessFormData = new FormData()
    businessFormData.append('business_id', route.params.id)
    const venueFormData = new FormData()
    venueFormData.append('business_id', route.params.id)
    venueFormData.append('venue_id', route.params.venueId)
    const [businessResult, venueResult] = await Promise.allSettled([
      api.editBusinessVendor(businessFormData),
      api.listVenuesBusinessVendor(venueFormData)
    ])

    let hasPartialFailure = false

    if (businessResult.status === 'fulfilled') {
      const businessPayload = Array.isArray(businessResult.value?.data?.data)
        ? businessResult.value.data.data[0]
        : businessResult.value?.data?.data || businessResult.value?.data || {}
      businessName.value = businessPayload?.business_name || businessPayload?.businessName || businessPayload?.name || businessName.value
    } else {
      hasPartialFailure = true
    }

    if (venueResult.status === 'fulfilled') {
      const venuePayload = venueResult.value?.data?.data || venueResult.value?.data || {}
      const venueItems = Array.isArray(venuePayload)
        ? venuePayload
        : venuePayload?.venues || venuePayload?.items || venuePayload?.list || venuePayload?.venue_list || []
      const matchedVenue = Array.isArray(venueItems)
        ? venueItems.find((item) => String(item?.id || item?.venue_id || item?.vendor_business_venue_id || '') === String(route.params.venueId))
        : null
      venue.value = normalizeVenue(matchedVenue || venueItems[0] || {})
    } else {
      hasPartialFailure = true
      venue.value = normalizeVenue({
        id: route.params.venueId,
        title: route.query.venueTitle || 'Venue detail',
        address: route.query.venueAddress || '',
        status: 'Draft'
      })
    }

    const spacesFormData = new FormData()
    spacesFormData.append('venue_id', route.params.venueId)
    const packagesFormData = new FormData()
    packagesFormData.append('venue_id', route.params.venueId)
    const partnersFormData = new FormData()
    partnersFormData.append('venue_id', route.params.venueId)
    const policiesFormData = new FormData()
    policiesFormData.append('venue_id', route.params.venueId)

    const [spacesResult, packagesResult, partnersResult, policiesResult, galleryResult] = await Promise.allSettled([
      api.listVenueSpaces(spacesFormData),
      api.getPackagesVenue(packagesFormData),
      api.listVenueVendorPartners(partnersFormData),
      api.editPoliciesVenue(policiesFormData),
      loadVenueGalleryPreview(String(route.params.venueId))
    ])

    if (spacesResult.status === 'fulfilled') {
      const spaceData = spacesResult.value?.data?.data || spacesResult.value?.data || {}
      const items = Array.isArray(spaceData) ? spaceData : spaceData?.spaces || spaceData?.items || spaceData?.list || []
      spaces.value = items.map((item, index) => normalizeSpace(item, index))
      selectedSpaceId.value = spaces.value[0]?.id || ''
    } else {
      spaces.value = []
      selectedSpaceId.value = ''
    }

    if (packagesResult.status === 'fulfilled') {
      const packageData = packagesResult.value?.data?.data || packagesResult.value?.data || {}
      const items = Array.isArray(packageData) ? packageData : packageData?.packages || packageData?.items || packageData?.list || []
      packages.value = items.map((item, index) => normalizePackage(item, index))
    } else {
      packages.value = []
    }

    if (partnersResult.status === 'fulfilled') {
      const partnerData = partnersResult.value?.data?.data || partnersResult.value?.data || {}
      const items = Array.isArray(partnerData) ? partnerData : partnerData?.partners || partnerData?.vendor_partners || partnerData?.items || partnerData?.list || []
      partners.value = items.map((item, index) => normalizePartner(item, index))
    } else {
      partners.value = []
      hasPartialFailure = true
    }

    if (policiesResult.status === 'fulfilled') {
      const policyItem = normalizeApiCollection(policiesResult.value?.data, ['policies', 'policy', 'items', 'list'])[0] || policiesResult.value?.data?.data || policiesResult.value?.data || {}
      policies.value = {
        inHouseCatering: policyItem?.in_house_catering || '',
        outsideCatering: policyItem?.outside_catering || '',
        cateringFoodType: policyItem?.catering_food_type || policyItem?.food_type || policyItem?.veg_non_veg || '',
        cateringPriceUnit: policyItem?.catering_price_unit || policyItem?.price_unit || policyItem?.price_per || '',
        decorPolicy: policyItem?.decor_policy || '',
        musicPolicy: policyItem?.music_policy || '',
        alcoholInhouse: policyItem?.alcohol_inhouse || '',
        alcoholOutside: policyItem?.alcohol_outside || '',
        minGuests: policyItem?.min_guests || '',
        maxGuests: policyItem?.max_guests || '',
        decorTime: policyItem?.decor_time || '',
        cleanTime: policyItem?.clean_time || '',
        noiseMusic: policyItem?.noise_music || '',
        otherHouseRules: policyItem?.other_house_rules || '',
        cancelTerms: policyItem?.cancel_terms || '',
        depositPercent: policyItem?.deposit_percent || ''
      }
    } else {
      hasPartialFailure = true
    }

    if (galleryResult.status === 'fulfilled') {
      gallery.value = galleryResult.value
    } else {
      gallery.value = []
      hasPartialFailure = true
    }

    if (hasPartialFailure) {
      error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
    }
  } catch (err) {
    error.value = 'Some venue details could not be loaded. Showing the preview with fallback content.'
  } finally {
    loading.value = false
  }
}

const destroyGalleryLightbox = () => {
  if (galleryLightbox.value) {
    galleryLightbox.value.destroy(true)
    galleryLightbox.value = null
  }
}

const openVenueGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyGalleryLightbox()
  galleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.thumb,
      subHtml: image.tagName ? `<span class="text-sm font-semibold">${image.tagName}</span>` : ''
    })),
    index: Math.max(0, startIndex),
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false,
    mobileSettings: {
      controls: true,
      showCloseIcon: true,
      download: false
    }
  })
  galleryLightbox.value.openGallery(startIndex)
}

const openVenueGalleryById = (image) => {
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openVenueGallery(startIndex >= 0 ? startIndex : 0)
}

const goBack = () => {
  router.push({
    name: 'business-module-edit',
    params: {
      businessId: route.params.id,
      moduleType: 'venue',
      moduleId: route.params.venueId
    },
    query: {
      businessName: businessName.value,
      section: 'preview'
    }
  })
}

onMounted(() => {
  loadVenue()
})

onBeforeUnmount(() => {
  destroyGalleryLightbox()
})
</script>
